import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  Typography,
} from '@mui/material';
import { Add, Edit, PersonRemove } from '@mui/icons-material';

import type { TravelerResponseDto } from '@bandido/shared/dtos';

import { maskDocumentNumber, travelerDisplayName } from '../travelerForm';

interface Props {
  traveler: TravelerResponseDto;
  mode: 'roster' | 'suggestion';
  busy?: boolean;
  onEdit?: (traveler: TravelerResponseDto) => void;
  onAssociate?: (traveler: TravelerResponseDto) => void;
  onRemove?: (traveler: TravelerResponseDto) => void;
  onViewEvidence?: (traveler: TravelerResponseDto) => void;
}

export const TravelerCard = ({
  traveler,
  mode,
  busy,
  onEdit,
  onAssociate,
  onRemove,
  onViewEvidence,
}: Props) => {
  const { t } = useTranslation();

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Box
        display="flex"
        flexDirection={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        gap={1}
      >
        <Box minWidth={0}>
          <Typography variant="subtitle2" noWrap title={travelerDisplayName(traveler)}>
            {travelerDisplayName(traveler)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {traveler.birthDate
              ? t('travelers.birthDateValue', { date: traveler.birthDate })
              : t('travelers.noBirthDate')}
          </Typography>
        </Box>
        <Box display="flex" flexWrap="wrap" alignItems="flex-start" gap={1}>
          {mode === 'roster' ? (
            <>
              {onViewEvidence && <Button size="small" disabled={busy} onClick={() => onViewEvidence(traveler)}>{t('copilot.capture.evidence')}</Button>}
              <Button
                size="small"
                startIcon={<Edit />}
                onClick={() => onEdit?.(traveler)}
                disabled={busy}
              >
                {t('common.actions.edit')}
              </Button>
              <Button
                size="small"
                color="error"
                startIcon={<PersonRemove />}
                onClick={() => onRemove?.(traveler)}
                disabled={busy}
              >
                {t('travelers.actions.remove')}
              </Button>
            </>
          ) : (
            <Button
              size="small"
              variant="outlined"
              startIcon={<Add />}
              onClick={() => onAssociate?.(traveler)}
              disabled={busy}
            >
              {t('travelers.actions.addToCase')}
            </Button>
          )}
        </Box>
      </Box>

      {traveler.documents.length > 0 && (
        <>
          <Divider sx={{ my: 1.5 }} />
          <Box display="flex" flexWrap="wrap" gap={1}>
            {traveler.documents.map((document) => (
              <Chip
                key={document.id}
                size="small"
                variant="outlined"
                label={`${document.type.toUpperCase()} · ${maskDocumentNumber(document.number)}`}
                title={t('travelers.documents.maskedHint')}
              />
            ))}
          </Box>
        </>
      )}
    </Paper>
  );
};
